import './App.css';
import { useEffect, useState } from 'react';
import LoginForm from './components/loginForm';
import ForgotPassword from './components/forgotPassword';
import ResetPassword from './components/resetPassword';
import SalesList from './components/sales/salesList';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { ProtectedRoute } from './routes/protectedRoute';
import { UserProvider, useUser } from './context/userContext';
import { getConfig } from './api/systemConfigApi';
import MaintenancePage from './components/common/MaintenancePage';
import CreateSalesForm from './components/sales/createSalesForm';
import QuizGateNotice from './components/sales/quizGateNotice';
import CommissionDetail from './components/commission/commissionDetail';
import AdminSalesList from './components/admin/adminSalesList';
import SalesCommissionList from './components/accountant/salesCommissionList';
import WebbeyCommissionList from './components/accountant/webbeyCommissionList';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import { UserProfile } from './components/agent/userProfile';
import MainLayout from './components/layout/MainLayout';
import { NewDashboard } from './components/dashboard/newDashboard';
import CompetitiveAnalysis2026 from './components/saleskit/CompetitiveAnalysis2026';
import ProductCatalogue from './components/saleskit/ProductCatalogue';
import SalesKit from './components/saleskit/SalesKit';
import NotFound from './components/common/notFound';
import AsternClaimList from './components/accountant/asternClaimList';
import { AgentCommissionProgressList } from './components/commission/agentCommissionProgressList';
import MocList from './components/commission/mocList';
import GroupSalesList from './components/sales/groupSalesList';
import SchedulingPage from './components/sales/schedulingPage';
// v1 RSC archived to _deprecated/ — V2 is the active component at /tools/rsc
import ResidentialSolarCalculatorV2 from './components/sales/residentialSolarCalculatorV2';
import CommercialSalesCalculator from './components/sales/commercialSolarCalculator';
import BillCalculator from './components/sales/billCalculator';
import UserManagement from './components/admin/userManagement';
import CustomerList from './components/customer/customerList';
import CustomerPage from './components/customer/customerPage';
import AccountingOverview from './components/accountant/accountingOverview';
import AgentHierarchy from './components/admin/agentHierarchy';
import MyTeam from './components/team/myTeam';
import EditSalePage from './components/admin/editSalePage';
import EditUserPage from './components/admin/editUserPage';
import EditCommissionPage from './components/accountant/editCommissionPage';
import PricingPage from './components/resources/pricingTab';
import AddonsPage from './components/resources/addonsTab';
import DocumentsPage from './components/resources/documentsTab';
import PermissionManager from './components/settings/permissionManager';
import VersionInfo from './components/settings/versionInfo';
import NotificationList from './components/notifications/notificationList';
import SalesReport from './components/reports/salesReport';
import RankingsPage from './components/rankings/rankingsPage';
import PayoutReport from './components/reports/payoutReport';
import AgentReport from './components/reports/agentReport';
import PendingApprovals from './components/approvals/pendingApprovals';
import ApproverConfig from './components/settings/approverConfig';
import ImportSales from './components/settings/importSales';
import NoticeboardManagement from './components/settings/noticeboardManagement';
import QuizQuestionsAdmin from './components/settings/quizQuestionsAdmin';
import ActiveCampaigns from './components/incentives/activeCampaigns';
import CampaignDetail from './components/incentives/campaignDetail';
import ManageCampaigns from './components/incentives/manageCampaigns';
import CampaignDetailPage from './components/incentives/campaignDetailPage';
import SalesCommissionDetail from './components/accounting/salesCommissionDetail';
import AgentSignup from './components/public/agentSignup';
import SignPage from './components/public/signPage';
import ReferralLandingPage from './components/public/referralLandingPage';
import AgreementGate from './components/public/agreementGate';
import ApplicationStatus from './components/public/applicationStatus';
import BlockedScreen from './components/public/blockedScreen';
import AllSlipsList from './components/accountant/allSlipsList';
import Cp58List from './components/accountant/cp58List';
import QuotationPreview from './components/tools/quotationPreview';
import CommercialQuotationPreview from './components/tools/commercialQuotationPreview';
import useAppPolling from './hooks/useAppPolling';
import UpdateBanner from './components/common/updateBanner';
import FaqPage from './components/tools/faqPage';
import TutorialsReader from './components/learn/TutorialsReader';
import TutorialFormPage from './components/learn/TutorialFormPage';
import QuizPage from './components/learn/quizPage';
import EventsPage from './components/events/eventsPage';
import TrainingRoom from './components/events/trainingRoom';
import EventFormPage from './components/events/eventFormPage';
import ActivityReport from './components/reports/activityReport';
import SopPage from './components/sop/sopPage';
import SupportList from './components/support/supportList';
import SupportTicketPage from './components/support/supportTicketPage';
import SupportNewPage from './components/support/supportNewPage';
import MyCommissionSlips from './components/commission/myCommissionSlips';
import MyIncentiveClaimsList from './components/commission/myIncentiveClaimsList';
import UserPermissions from './components/settings/userPermissions';
import ActivityLogs from './components/settings/activityLogs';
import CommissionReport from './components/reports/commissionReport';
import IncentiveReport from './components/reports/incentiveReport';
import CommissionAgentDetail from './components/reports/commissionAgentDetail';
import ReferralReport, { ReferralReferrerDetail } from './components/reports/referralReport';
import TierSettings from './components/admin/tierSettings';
import WhatsappSettings from './components/settings/whatsappSettings';
import GeneralSettings from './components/settings/generalSettings';
import DesignRequestList from './components/design/DesignRequestList';
import RequestDesignForm from './components/design/RequestDesignForm';
import AllRfList from './components/accountant/allRfList';
import ReferralFeeDetail from './components/accountant/referralFeeDetail';
import ReferralDashboard from './components/referral/referralDashboard';
import ReferralLeadsPage from './components/team/referralLeadsPage';
import AllMucList from './components/accountant/allMucList';
import MyReferrersPage from './components/team/myReferrersPage';
import ReferrerEditPage from './components/team/referrerEditPage';

function RedirectWithParams({ from, to }) {
  const params = useParams();
  const newPath = to + '/' + (params.salesId || '');
  return <Navigate to={newPath} replace />;
}

function AppContent() {
  const { updateAvailable } = useAppPolling();

  // Disable mouse-wheel value-stepping on all number inputs across the app.
  // Native HTML number inputs change value when the wheel fires over a focused
  // field — annoying when the user is mid-scroll. We blur on wheel so the page
  // scrolls instead and the input value stays put.
  useEffect(() => {
    const handler = () => {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number') {
        el.blur();
      }
    };
    document.addEventListener('wheel', handler, { passive: true });
    return () => document.removeEventListener('wheel', handler);
  }, []);

  // ---- Maintenance mode gate ----
  // When Super Admin turns Maintenance Mode on, every logged-in non-Super-Admin user
  // sees the MaintenancePage, EXCEPT on pricing tools (/tools/*), resources
  // (/resources/*) and tutorials. Super Admin is never blocked (so they can turn it off).
  const { user } = useUser();
  const location = useLocation();
  const [maintenance, setMaintenance] = useState(false);
  useEffect(() => {
    let active = true;
    const check = () => getConfig('MaintenanceMode')
      .then(r => { if (active) setMaintenance(String(r?.data?.value).toLowerCase() === 'true'); })
      .catch(() => {});
    check();
    const t = setInterval(check, 60000);
    return () => { active = false; clearInterval(t); };
  }, [location.pathname]);
  const isSuperAdmin = !!user?.roles?.includes('Super Admin');
  // /quiz belongs here too: a trainee has nowhere else to be, and maintenance mode
  // was showing them the maintenance page on the one task they are allowed to do.
  const maintAllowed = ['/tools', '/resources', '/tutorials', '/quiz', '/events', '/user/profile'].some(p => location.pathname.startsWith(p));
  if (maintenance && user && !isSuperAdmin && !maintAllowed) {
    return <MaintenancePage />;
  }

  return (
    <div>
      <UpdateBanner show={updateAvailable} />
      <Routes>
        <Route path="/" element={<LoginForm />} />
        {/* /login is an alias for / so links from email templates ({BaseUrl}/login) land correctly */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/join/:token" element={<AgentSignup />} />
        <Route path="/sign/:token" element={<SignPage />} />
        {/* A referrer's share link. Public on purpose: the person opening it has no
            account and never will. (user 2026-10-18) */}
        <Route path="/r/:code" element={<ReferralLandingPage />} />
        <Route path="/accept-agreement" element={
          <ProtectedRoute bypassAgreementGate>
            <AgreementGate />
          </ProtectedRoute>
        } />
        <Route path="/application-status" element={
          <ProtectedRoute bypassApplicationGate bypassAgreementGate>
            <ApplicationStatus />
          </ProtectedRoute>
        } />
        {/* Public — inactivity-blocked users land here. Not wrapped in ProtectedRoute,
            since ProtectedRoute's accessBlocked gate would redirect right back here
            anyway; this avoids relying on that redirect not looping. */}
        <Route path="/blocked" element={<BlockedScreen />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <MainLayout>
              <NewDashboard />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* The three kit documents carry SalesKitAccess, which only Super Admin holds
            (user 2026-09-27). They used to need nothing but a login, so every agent
            could open the competitive battlecards by URL. The API enforces the same
            right, so the link is not a back door either. */}
        <Route path="/competitiveanalysis2026" element={
          <ProtectedRoute requiredRoleRight={["SalesKitAccess"]}>
            <CompetitiveAnalysis2026 />
          </ProtectedRoute>
        } />
        <Route path="/sales-kit" element={
          <ProtectedRoute requiredRoleRight={["SalesKitAccess"]}>
            <SalesKit />
          </ProtectedRoute>
        } />
        <Route path="/product-catalogue" element={
          <ProtectedRoute requiredRoleRight={["SalesKitAccess"]}>
            <ProductCatalogue />
          </ProtectedRoute>
        } />
        <Route path="/sales" element={
          <ProtectedRoute requiredRoleRight={["AgentSalesList"]}>
            <MainLayout>
              <SalesList />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Scheduling. ANY of the three sales-list rights opens it, because all three
            kinds of person schedule: an agent chases their own jobs (AgentSalesList),
            a group admin their group's (GroupSalesList), and the office everything
            (AllSalesList). Who sees WHOSE jobs is decided inside GetSchedule, not by
            the route, so the door can be wide while the room stays scoped.
            Gating on AgentSalesList alone locked out the one person who schedules for
            a living: Joyce is Admin + Group Admin and holds AllSalesList, not
            AgentSalesList. (user 2026-10-05: "so need to allow access to her")
            A static path outranks /sales/:id, so this is never read as a sale id.
            (user 2026-10-04: "the scheduling page, put under sales. All Scheduling.
            so each of their sales can see their own scheduling only") */}
        <Route path="/sales/scheduling" element={
          <ProtectedRoute requiredRoleRight={["AgentSalesList", "AllSalesList", "GroupSalesList"]}>
            <MainLayout>
              <SchedulingPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/sales/group" element={
          <ProtectedRoute requiredRoleRight={["GroupSalesList"]}>
            <MainLayout>
              <GroupSalesList />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Two separate gates, in order. The quiz says they are certified to sell; the
            profile says we can pay them and file their CP58. An incomplete profile sends
            them STRAIGHT to the profile page rather than showing a notice they would have
            to act on themselves — the banner there already lists what is missing.
            (user 2026-10-23: "when they click create sales page, will redicret to profile
             to fill up") */}
        <Route path="/sales/create" element={
          <ProtectedRoute requiredRoleRight={["CreateSale"]}>
            <MainLayout>
              {user?.canCreateSale === false
                ? <QuizGateNotice />
                : (user?.profileMissing?.length > 0
                  ? <Navigate to="/user/profile" replace />
                  : <CreateSalesForm />)}
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/user/profile" element={
          <ProtectedRoute>
            <MainLayout>
              <UserProfile />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/psc" element={
          <ProtectedRoute requiredRoleRight={["PersonalSalesCommission"]}>
            <MainLayout>
              <AgentCommissionProgressList commPayType="PSC" />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/doc" element={
          <ProtectedRoute requiredRoleRight={["DownlineOverridingCommission"]}>
            <MainLayout>
              <AgentCommissionProgressList commPayType="DOC" />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/toc" element={
          <ProtectedRoute requiredRoleRight={["TeamOverridingCommission"]}>
            <MainLayout>
              <AgentCommissionProgressList commPayType="TOC" />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/moc" element={
          <ProtectedRoute requiredRoleRight={["ManagerOverridingCommission"]}>
            <MainLayout>
              <MocList />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* MUC — the agent's own markup commission. Gated on the same right as PSC
            because it IS their own selling commission, just the mark-up half of it.
            (user 2026-11-02) */}
        <Route path="/commissions/muc" element={
          <ProtectedRoute requiredRoleRight={["PersonalSalesCommission"]}>
            <MainLayout>
              <AgentCommissionProgressList commPayType="MUC" />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/referral" element={
          <ProtectedRoute requiredRoleRight={["PersonalSalesCommission"]}>
            <MainLayout>
              <AgentCommissionProgressList commPayType="RefC" />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/detail/:commProgressId" element={
          <ProtectedRoute>
            <MainLayout>
              <CommissionDetail />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/mySlips" element={
          <ProtectedRoute requiredRoleRight={["PersonalSalesCommission"]}>
            <MainLayout>
              <MyCommissionSlips />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/commissions/incentives" element={
          <ProtectedRoute requiredRoleRight={["PersonalSalesCommission"]}>
            <MainLayout>
              <MyIncentiveClaimsList />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/sales/customers" element={
          <ProtectedRoute>
            <MainLayout>
              <CustomerList />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* One customer, full width — replaces the old dialog. Two paths so the
            person stays in the module they came from and the sidebar does not jump. */}
        <Route path="/sales/customers/:customerId" element={
          <ProtectedRoute>
            <MainLayout>
              <CustomerPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/customers" element={
          <ProtectedRoute>
            <MainLayout>
              <CustomerList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/customers/:customerId" element={
          <ProtectedRoute>
            <MainLayout>
              <CustomerPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* My Commission Report — every earner sees their own; Leaders also get Group. */}
        <Route path="/reports/commission" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin", "Leader", "Sales Agent", "In-house Agent"]}>
            <MainLayout>
              <CommissionReport />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* All Commission Report — company-wide. Its own route so the guard is at the
            door: an agent cannot load the page at all, rather than merely not seeing a
            tab on a page they can open. (user 2026-08-26) */}
        <Route path="/reports/commission/all" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin"]}>
            <MainLayout>
              <CommissionReport allScope />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/commission/agent/:agentId" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <CommissionAgentDetail />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* The referrers a SALES PERSON introduced — theirs by UplineId. Caller-scoped
            with no id to pass, so no right is needed beyond being logged in; somebody who
            has introduced nobody simply sees an empty page. Distinct from the referral
            REPORT, which follows the sales rather than the people. (user 2026-10-20) */}
        <Route path="/team/referrals" element={
          <ProtectedRoute>
            <MainLayout>
              <MyReferrersPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* The introductions those referrers sent in. Scoped by the SAME rule the list
            above uses — Super Admin and Accountant see every lead, everybody else sees the
            leads of referrers they introduced — and enforced on the server, so there is
            nothing to widen from here.
            (user 2026-10-31: "have another page is Referral Leads") */}
        <Route path="/team/referral-leads" element={
          <ProtectedRoute>
            <MainLayout>
              <ReferralLeadsPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Full-width edit, shaped like the admin user page but over the fields a sales
            person may change — and reached through the caller-scoped list, so typing a
            different id into the address bar finds nothing. (user 2026-10-21) */}
        <Route path="/team/referrals/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <ReferrerEditPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* A referrer's own dashboard. No right needed beyond being logged in: the endpoint
            behind it is keyed on the caller's own id and takes no parameter, so there is
            nothing to widen, and anyone with no referrals simply sees an empty page.
            (user 2026-10-18) */}
        <Route path="/referral/dashboard" element={
          <ProtectedRoute>
            <MainLayout>
              <ReferralDashboard />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Referral Report — company-wide, same guard as All RF. An Astern-borne fee is a
            company cost rather than anyone's commission, so this is not an agent's report
            and does not belong behind an agent-facing role. */}
        <Route path="/reports/referral" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <ReferralReport />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* A selling agent's own slice. No right needed beyond being logged in: the endpoint
            behind it is scoped to the caller and takes no agent id, so there is nothing to
            widen. Bank details are stripped server-side. (user 2026-10-14) */}
        <Route path="/reports/referral/mine" element={
          <ProtectedRoute>
            <MainLayout>
              <ReferralReport mine />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/referral/:referrerId" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <ReferralReferrerDetail />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/sales" element={
          <ProtectedRoute requiredRoleRight={["AllSalesList"]}>
            <MainLayout>
              <AdminSalesList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/sales/:salesId" element={
          <ProtectedRoute>
            <MainLayout>
              <EditSalePage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <UserManagement />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/users/:userId" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <EditUserPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/hierarchy" element={
          <ProtectedRoute requiredRoleRight={["AgentHierarchy"]}>
            <MainLayout>
              <AgentHierarchy />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/team/my-team" element={
          <ProtectedRoute requiredRole={["Super Admin", "Leader", "Sales Agent", "In-house Agent"]}>
            <MainLayout>
              <MyTeam />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/tiers" element={
          <ProtectedRoute requiredRole={["Super Admin"]}>
            <MainLayout>
              <TierSettings />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/whatsapp" element={
          <ProtectedRoute requiredRole={["Super Admin"]}>
            <MainLayout>
              <WhatsappSettings />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/general" element={
          <ProtectedRoute requiredRole={["Super Admin"]}>
            <MainLayout>
              <GeneralSettings />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/design-requests" element={
          <ProtectedRoute requiredRoleRight={["DesignRequestAccess", "CreateSale"]}>
            <MainLayout>
              <DesignRequestList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/design-requests/new" element={
          <ProtectedRoute requiredRoleRight={["DesignRequestAccess", "CreateSale"]}>
            <MainLayout>
              <RequestDesignForm />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/design-requests/:id/edit" element={
          <ProtectedRoute requiredRoleRight={["DesignRequestAccess", "CreateSale"]}>
            <MainLayout>
              <RequestDesignForm />
            </MainLayout>
          </ProtectedRoute>
        } />
<Route path="/accounting/asternClaims" element={
          <ProtectedRoute requiredRoleRight={["TotalCommission"]}>
            <MainLayout>
              <AsternClaimList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/commission/:commProgressId" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <EditCommissionPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Commission + incentive data — Accountant / Super Admin only, matching the menu.
            The route asked for AllSalesList, which Admin and Astern Admin also hold, so they
            could open this page by URL. The API refused the data (AllSalesCommList), so it
            showed empty rather than leaking — but the guard disagreeing with its own menu is
            one right-change away from a real leak. (user 2026-08-28) */}
        <Route path="/accounting/salesCommissions" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <SalesCommissionList />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Referral fees. Same guard as All Sales Comm deliberately: it is the same
            class of information to the same people, and a weaker guard here would be a
            way around that one. */}
        {/* All MUC — the accountant's view. Same guard as All Sales Comm: it is the
            same money, listed by one pay type. (user 2026-11-02) */}
        <Route path="/accounting/allMuc" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <AllMucList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/referralFees" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <AllRfList />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* One referral, keyed on the SALE — an Astern-borne fee has no CommissionProgress
            row to route on. Same guard as the list it is reached from. */}
        <Route path="/accounting/referral/:salesId" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <ReferralFeeDetail />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/webbeyCommissions" element={
          <ProtectedRoute requiredRoleRight={["WebbeyCommission"]}>
            <MainLayout>
              <WebbeyCommissionList />
            </MainLayout>
          </ProtectedRoute>
        } />
{/* Superseded by /accounting/slips (AllSlipsList). Kept as redirect so old
            bookmarks + CommissionSlip notification deep-links still land. */}
        <Route path="/accounting/commissionSlips" element={<Navigate to="/accounting/slips" replace />} />
        <Route path="/accounting/slips" element={
          <ProtectedRoute requiredRoleRight={["CommissionSlipGenerator", "PendingApprovals", "AllSalesCommList"]}>
            <MainLayout>
              <AllSlipsList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/accounting/cp58" element={
          <ProtectedRoute requiredRoleRight={["Cp58Generator"]}>
            <MainLayout>
              <Cp58List />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tools/csc" element={
          <ProtectedRoute requiredRoleRight={["CscTool"]}>
            <MainLayout>
              <CommercialSalesCalculator />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tools/rsc" element={
          <ProtectedRoute requiredRoleRight={["RscTool"]}>
            <MainLayout>
              <ResidentialSolarCalculatorV2 />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tools/bill" element={
          <ProtectedRoute requiredRoleRight={["BillTool"]}>
            <MainLayout>
              <BillCalculator />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tools/faq" element={
          <ProtectedRoute>
            <MainLayout>
              <FaqPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tutorials" element={
          <ProtectedRoute>
            <MainLayout>
              <TutorialsReader />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tutorials/new" element={
          <ProtectedRoute>
            <MainLayout>
              <TutorialFormPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/tutorials/:id/edit" element={
          <ProtectedRoute>
            <MainLayout>
              <TutorialFormPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/quiz" element={
          <ProtectedRoute>
            <MainLayout>
              <QuizPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Events. /events and the room are open to anyone with a login:
            the API resolves each event's own audience, so what comes back is already
            only what this person may see. A trainee who has not passed the quiz
            reaches them too, on purpose - onboarding training is for exactly the
            people the training gate holds back. (user 2026-10-03) */}
        <Route path="/events" element={
          <ProtectedRoute>
            <MainLayout>
              <EventsPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/events/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <TrainingRoom />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Create and Edit are full PAGES, not a dialog: the form carries five
            sections and a repeating audience editor, which scrolled inside a box
            inside the page with Save below the fold. (user 2026-10-03) */}
        <Route path="/events/new" element={
          <ProtectedRoute requiredRole={["Super Admin", "Admin"]}>
            <MainLayout>
              <EventFormPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/events/:id/edit" element={
          <ProtectedRoute requiredRole={["Super Admin", "Admin"]}>
            <MainLayout>
              <EventFormPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Both old admin paths now land on the one list - create, edit and delete
            live in it, so there is nowhere else to go. (user 2026-10-03) */}
        <Route path="/events/manage" element={
          <ProtectedRoute><MainLayout><EventsPage /></MainLayout></ProtectedRoute>
        } />
        <Route path="/settings/events" element={
          <ProtectedRoute><MainLayout><EventsPage /></MainLayout></ProtectedRoute>
        } />
        <Route path="/reports/activity" element={
          <ProtectedRoute>
            <MainLayout>
              <ActivityReport />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* The operations SOP. Behind the SopAccess right, checked by the API on
            every fetch - the document names staff and describes how money moves.
            (user 2026-10-03) */}
        <Route path="/resources/sop" element={
          <ProtectedRoute>
            <MainLayout>
              <SopPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* The same page, one step deep, so a step can be linked to and the back
            button walks back through the steps that were read. */}
        <Route path="/resources/sop/:page" element={
          <ProtectedRoute>
            <MainLayout>
              <SopPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Support. Anyone with a login can raise a ticket; the desk view is
            route-guarded AND right-checked, so a changed URL gets nothing. */}
        {/* A table of tickets; a row opens its own page, and raising one is a page
            too. The static paths outrank /support/:id in React Router's ranking,
            so /support/new and /support/all are never read as an id.
            (user 2026-10-03) */}
        <Route path="/support" element={
          <ProtectedRoute><MainLayout><SupportList /></MainLayout></ProtectedRoute>
        } />
        <Route path="/support/new" element={
          <ProtectedRoute><MainLayout><SupportNewPage /></MainLayout></ProtectedRoute>
        } />
        <Route path="/support/all" element={
          <ProtectedRoute><MainLayout><SupportList deskScope /></MainLayout></ProtectedRoute>
        } />
        <Route path="/support/:id" element={
          <ProtectedRoute><MainLayout><SupportTicketPage /></MainLayout></ProtectedRoute>
        } />
        <Route path="/reports/activity/all" element={
          <ProtectedRoute requiredRole={["Super Admin", "Admin"]}>
            <MainLayout>
              <ActivityReport allScope />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/quotation-preview" element={
          <ProtectedRoute>
            <QuotationPreview />
          </ProtectedRoute>
        } />
        <Route path="/commercial-quotation-preview" element={
          <ProtectedRoute>
            <CommercialQuotationPreview />
          </ProtectedRoute>
        } />

        {/* The matrix edits every other permission, so it is Super Admin by ROLE.
            Gating it on a right would let that right be granted away from inside
            the matrix itself. (user 2026-09-27) */}
        <Route path="/settings/permissions" element={
          <ProtectedRoute requiredRole={["Super Admin"]}>
            <MainLayout>
              <PermissionManager />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/version" element={
          <ProtectedRoute>
            <MainLayout>
              <VersionInfo />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/noticeboard" element={
          <ProtectedRoute requiredRoleRight={["NoticeBoard"]}>
            <MainLayout>
              <NoticeboardManagement />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/quiz-questions" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <QuizQuestionsAdmin />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/settings/user-permissions/:userId" element={
          <ProtectedRoute requiredRole={["Super Admin"]}>
            <MainLayout>
              <UserPermissions />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/notifications" element={
          <ProtectedRoute>
            <MainLayout>
              <NotificationList />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/resources/pricing" element={
          <ProtectedRoute requiredRoleRight={["AtapPricing"]}>
            <MainLayout>
              <PricingPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/resources/addons" element={
          <ProtectedRoute requiredRoleRight={["AddonPricing"]}>
            <MainLayout>
              <AddonsPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/resources/documents" element={
          <ProtectedRoute>
            <MainLayout>
              <DocumentsPage />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/reports/sales" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin", "Leader", "Sales Agent", "In-house Agent"]}>
            <MainLayout>
              <SalesReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/rankings" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin", "Leader", "Sales Agent", "In-house Agent", "Referral"]}>
            <MainLayout>
              <RankingsPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        {/* Webbey Report removed 2026-08-26 — the Payout Report supersedes it: same
            Total Comm / Webbey / Outstanding figures, plus the Nett-vs-Webbey drift
            reconciliation and incentive + extra slips. (user) */}
        {/* Incentive Report — split the same way as the commission reports.
            Separate from the Commission Report because incentive is per-agent PER
            CAMPAIGN and case incentives are banded on an agent total, not attributable
            to one sale. My = the agent's own; All = company-wide, Accountant / Super
            Admin only. (user 2026-08-27) */}
        <Route path="/reports/incentive" element={
          <ProtectedRoute>
            <MainLayout>
              <IncentiveReport />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/incentive/all" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin"]}>
            <MainLayout>
              <IncentiveReport allScope />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/payout" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin"]}>
            <MainLayout>
              <PayoutReport />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/reports/agents" element={
          <ProtectedRoute requiredRole={["Accountant", "Super Admin"]}>
            <MainLayout>
              <AgentReport />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/approvals" element={
          <ProtectedRoute>
            <MainLayout>
              <PendingApprovals />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/settings/approvers" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <ApproverConfig />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/settings/import" element={
          <ProtectedRoute requiredRoleRight={["ImportSales", "UserManagement"]}>
            <MainLayout>
              <ImportSales />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/settings/logs" element={
          <ProtectedRoute requiredRoleRight={["UserManagement"]}>
            <MainLayout>
              <ActivityLogs />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* /admin/applications consolidated into /approvals "Agent Applications" tab (v1.5.24).
            Kept as redirect so any bookmarks/notification redirects still land somewhere sensible. */}
        <Route path="/admin/applications" element={<Navigate to="/approvals" replace />} />

        <Route path="/incentives" element={
          <ProtectedRoute>
            <MainLayout>
              <ActiveCampaigns />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/incentives/manage" element={
          <ProtectedRoute requiredRoleRight={["IncentiveManagement"]}>
            <MainLayout>
              <ManageCampaigns />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/incentives/slips" element={<Navigate to="/accounting/slips?type=Incentive" replace />} />
        {/* Legacy IncentiveSlips page deprecated — unified into /accounting/slips. */}
        <Route path="/incentives/slips/legacy" element={<Navigate to="/accounting/slips?type=Incentive" replace />} />
        <Route path="/incentives/:campaignId/detail" element={
          <ProtectedRoute>
            <MainLayout>
              <CampaignDetailPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/incentives/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <CampaignDetail />
            </MainLayout>
          </ProtectedRoute>
        } />

        <Route path="/accounting/sales/:salesId/commission" element={
          <ProtectedRoute requiredRoleRight={["AllSalesCommList"]}>
            <MainLayout>
              <SalesCommissionDetail />
            </MainLayout>
          </ProtectedRoute>
        } />

        {/* Redirects for old routes */}
        <Route path="/admin/sales/:salesId" element={<RedirectWithParams from="/admin/sales" to="/accounting/sales" />} />
        <Route path="/admin/sales" element={<Navigate to="/accounting/sales" replace />} />
        <Route path="/customers" element={<Navigate to="/sales/customers" replace />} />
        <Route path="/groupSales" element={<Navigate to="/sales/group" replace />} />
        <Route path="/accounting/applications" element={<Navigate to="/admin/applications" replace />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <UserProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </UserProvider>
    </LocalizationProvider>
  );
}
